import { Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { normalizeTagName } from '@/features/projects/lib/tag-tone'
import type { ProjectTag } from '@/shared/types/domain'
import { cn } from '@/shared/utils/cn'

interface TagPickerProps {
  tags: ProjectTag[]
  selectedIds: string[]
  disabled?: boolean
  onPick: (payload: { name: string } | { tag_id: string }) => void
}

/**
 * Отдельный компонент, а не общий DropdownMenu: здесь нужны поле поиска и навигация
 * стрелками, которых у того нет, а переделывать общий компонент ради одной фичи незачем.
 */
export function TagPicker({ tags, selectedIds, disabled, onPick }: TagPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const available = useMemo(
    () => tags.filter((tag) => !selectedIds.includes(tag.id)),
    [tags, selectedIds],
  )

  const matched = useMemo(() => {
    const normalizedQuery = normalizeTagName(query)

    if (!normalizedQuery) return available

    return available.filter((tag) => normalizeTagName(tag.name).includes(normalizedQuery))
  }, [available, query])

  const trimmedQuery = query.trim().replace(/\s+/g, ' ')
  const normalizedQuery = normalizeTagName(query)
  const canCreate =
    trimmedQuery.length > 0 && !tags.some((tag) => normalizeTagName(tag.name) === normalizedQuery)

  const options = useMemo(
    () => [
      ...matched.map((tag) => ({ key: tag.id, label: tag.name, payload: { tag_id: tag.id } })),
      ...(canCreate
        ? [
            {
              key: '__create__',
              label: `Создать тег «${trimmedQuery}»`,
              payload: { name: trimmedQuery },
            },
          ]
        : []),
    ],
    [matched, canCreate, trimmedQuery],
  )

  const close = () => {
    setOpen(false)
    setQuery('')
    setHighlight(0)
  }

  useEffect(() => {
    if (!open) return

    inputRef.current?.focus()

    const onDocClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        close()
      }
    }

    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const pick = (payload: { name: string } | { tag_id: string }) => {
    onPick(payload)
    close()
  }

  return (
    <div className="relative inline-flex" ref={wrapRef}>
      <button
        aria-label="Добавить тег"
        className="inline-flex h-6 items-center gap-1 rounded-sm border border-dashed border-[var(--panel-border)] px-2 text-xs text-[var(--color-text-soft)] transition hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] disabled:opacity-50"
        disabled={disabled}
        onClick={() => {
          if (open) {
            close()
          } else {
            setOpen(true)
            setHighlight(0)
          }
        }}
        type="button"
      >
        <Plus className="h-3 w-3" />
      </button>

      {open ? (
        <div className="absolute top-full left-0 z-30 mt-1 w-56 rounded-md border border-[var(--panel-border)] bg-[var(--panel)] p-1 shadow-md">
          <input
            className="mb-1 w-full rounded-sm border border-[var(--panel-border)] bg-transparent px-2 py-1 text-xs outline-none focus:border-[var(--color-accent)]"
            onChange={(event) => {
              setQuery(event.target.value)
              setHighlight(0)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault()
                close()
                return
              }

              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setHighlight((value) => Math.min(value + 1, Math.max(options.length - 1, 0)))
                return
              }

              if (event.key === 'ArrowUp') {
                event.preventDefault()
                setHighlight((value) => Math.max(value - 1, 0))
                return
              }

              if (event.key === 'Enter') {
                event.preventDefault()
                const option = options[highlight]

                if (option) pick(option.payload)
              }
            }}
            placeholder="Поиск или новый тег"
            ref={inputRef}
            value={query}
          />

          <div className="max-h-48 overflow-y-auto" role="listbox">
            {options.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-[var(--color-text-muted)]">
                Ничего не найдено
              </p>
            ) : (
              options.map((option, index) => (
                <button
                  aria-selected={index === highlight}
                  className={cn(
                    'block w-full truncate rounded-sm px-2 py-1.5 text-left text-xs transition',
                    index === highlight
                      ? 'bg-[var(--panel-subtle)] text-[var(--color-text)]'
                      : 'text-[var(--color-text-soft)] hover:bg-[var(--panel-subtle)]',
                  )}
                  key={option.key}
                  onClick={() => pick(option.payload)}
                  onMouseEnter={() => setHighlight(index)}
                  role="option"
                  type="button"
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
